import { useState, useEffect } from 'react'
import { useAuth } from '../context/AuthContext'
import { useTheme } from '../context/ThemeContext'
import { get, patch } from '../lib/api'
import Meetings from './Meetings'
import Reviews from './Reviews'
import Billing from './Billing'
import AccountSettings from './AccountSettings'
import EmployerPlanner from './EmployerPlanner'
import Employeroverview from './EmployerOverview'
import Employerteam from './EmployerTeam'
import Employercheckins from './EmployerCheckins'
import NotificationBell from '../components/NotificationBell'
import CalendarView from '../components/CalendarView'
import ExpenseLog from '../components/ExpenseLog'
import Jobs from '../components/Jobs'

// ── Theme tokens matching EmployeeDashboard exactly ─────────────────────────
const LIGHT = {
  bg: '#F8F9FA', sidebar: '#1E293B', white: '#FFFFFF',
  primary: '#4F46E5', primary2: '#6366F1',
  text: '#1E293B', textSub: '#475569', textMuted: '#9CA3AF',
  border: '#E5E7EB', success: '#10B981', amber: '#F59E0B',
  danger: '#EF4444', purple: '#7C3AED',
  card: '#FFFFFF', cardBorder: '#E5E7EB',
  input: '#F8F9FA', inputBorder: '#E5E7EB', inputText: '#1E293B',
  shadow: '0 1px 3px rgba(0,0,0,0.08)', shadowMd: '0 4px 6px rgba(0,0,0,0.07)',
  navHover: 'rgba(99,102,241,0.2)',
  surface: '#F1F5F9',
  green: '#10B981', greenSoft: '#ECFDF5',
  red: '#EF4444', redSoft: '#FEF2F2',
  blue: '#3B82F6',
  dark: false,
}
const DARK = {
  bg: '#0F172A', sidebar: '#0A0A0F', white: '#1E293B',
  primary: '#6366F1', primary2: '#818CF8',
  text: '#F1F5F9', textSub: '#94A3B8', textMuted: '#64748B',
  border: '#334155', success: '#10B981', amber: '#F59E0B',
  danger: '#EF4444', purple: '#A78BFA',
  card: '#1E293B', cardBorder: '#334155',
  input: '#0F172A', inputBorder: '#334155', inputText: '#F1F5F9',
  shadow: '0 1px 3px rgba(0,0,0,0.3)', shadowMd: '0 4px 6px rgba(0,0,0,0.3)',
  navHover: 'rgba(99,102,241,0.2)',
  surface: '#0F172A',
  green: '#10B981', greenSoft: '#052e16',
  red: '#EF4444', redSoft: '#2d0a0a',
  blue: '#3B82F6',
  dark: true,
}

const NAV_GROUPS = [
  { section: 'MAIN', items: [
    { id: 'overview',      icon: '🏠', label: 'Overview' },
    { id: 'team',          icon: '👥', label: 'Team' },
    { id: 'checkins',      icon: '✅', label: 'Check-in / Checkout' },
  ]},
  { section: 'PERFORMANCE', items: [
    { id: 'pdr_approvals', icon: '📋', label: 'PDR Approvals' },
    { id: 'meetings',      icon: '📅', label: 'Meetings' },
    { id: 'calendar',      icon: '🗓️', label: 'Calendar' },
    { id: 'reviews',       icon: '⭐', label: 'Reviews' },
    { id: 'jobs',          icon: '🧩', label: 'Job Requests' },
    { id: 'expenses',      icon: '🧾', label: 'Expense Log' },
    { id: 'planner',       icon: '📝', label: 'My Planner' },
  ]},
  { section: 'ACCOUNT', items: [
    { id: 'invite',        icon: '➕', label: 'Invite Employee' },
    { id: 'billing',       icon: '💳', label: 'Billing & Plans' },
    { id: 'settings',      icon: '⚙️',  label: 'Account Settings' },
  ]},
]

function getTime() {
  const h = new Date().getHours()
  return h < 12 ? 'morning' : h < 17 ? 'afternoon' : 'evening'
}

// ════════════════════════════════════════════════════════════════════════════
// MAIN
// ════════════════════════════════════════════════════════════════════════════
export default function EmployerDashboard() {
  const { profile, signOut } = useAuth()
  const [dark, setDark] = useState(() => localStorage.getItem('emp-theme') === 'dark')
  const t = dark ? DARK : LIGHT

  const [nav, setNav]               = useState('overview')
  const [meetingPreset, setMeetingPreset] = useState(null)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [employees, setEmployees]   = useState([])
  const [checkIns, setCheckIns]     = useState([])
  const [selectedEmp, setSelectedEmp] = useState(null)
  const [loading, setLoading]       = useState(true)
  const [pdrCounts, setPdrCounts]   = useState({}) // { empId: { pending, total } }
  const [reviews, setReviews]       = useState([])
  const [meetings, setMeetings]     = useState([])
  const [fullProfile, setFullProfile] = useState(null)

  function toggleDark() {
    const next = !dark
    setDark(next)
    localStorage.setItem('emp-theme', next ? 'dark' : 'light')
  }

  useEffect(() => { fetchData() }, [])

  async function fetchData() {
    setLoading(true)
    const [usersRes, insRes, pdrsRes, revRes, meetRes] = await Promise.all([
      get('/users'),
      get('/check-ins'),
      get('/pdrs'),
      get('/reviews'),
      get('/meetings'),
    ])

    const allUsers = usersRes.users || []
    const empMap = Object.fromEntries(allUsers.map((u) => [u.id, u]))
    setEmployees(allUsers.filter((u) => u.role === 'employee'))

    // attach employee name/email to each check-in (replaces Supabase nested select)
    const ins = (insRes.checkIns || []).map((c) => ({
      ...c,
      users: empMap[c.employee_id]
        ? { full_name: empMap[c.employee_id].full_name, email: empMap[c.employee_id].email }
        : null,
    }))
    setCheckIns(ins)
    setReviews(revRes.reviews || [])
    setMeetings(meetRes.meetings || [])
    setFullProfile(profile)

    // Build per-employee PDR counts
    const counts = {}
    for (const pdr of (pdrsRes.pdrs || [])) {
      if (!counts[pdr.employee_id]) counts[pdr.employee_id] = { pending: 0, total: 0 }
      counts[pdr.employee_id].total++
      if (pdr.status === 'pending_approval') counts[pdr.employee_id].pending++
    }
    setPdrCounts(counts)
    setLoading(false)
  }

  const today = new Date().toISOString().split('T')[0]
  const todayIns = checkIns.filter(c => c.date === today)
  const totalPending       = Object.values(pdrCounts).reduce((s, c) => s + c.pending, 0)
  const disputedCount      = reviews.filter(r => r.status === 'disputed').length
  const pendingMeetingRsvp = meetings.filter(m => m.status === 'scheduled' && new Date(m.meeting_date) > new Date()).length

  const P = { t, profile, employees, checkIns, todayIns, pdrCounts, selectedEmp, setSelectedEmp, onRefresh: fetchData }

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: t.bg, fontFamily: "'Inter', system-ui, sans-serif" }}>

      {/* ══ SIDEBAR ══ */}
      <div className={`eh-sidebar${sidebarOpen ? ' open' : ''}`} style={{ width: 250, background: t.sidebar, display: 'flex', flexDirection: 'column', position: 'fixed', top: 0, bottom: 0, zIndex: 50, overflowY: 'auto' }}>

        {/* Logo */}
        <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: 9, background: 'linear-gradient(135deg, #4F46E5 0%, #6366F1 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <span style={{ color: '#fff', fontWeight: 800, fontSize: 13 }}>EH</span>
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>Employee Hive</div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>{profile?.organizations?.name}</div>
            </div>
          </div>
        </div>

        {/* Employer badge */}
        <div style={{ padding: '1rem 1.5rem', borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', gap: 10 }}>
          {(fullProfile?.avatar_url || profile?.avatar_url) ? (
            <img src={fullProfile?.avatar_url || profile?.avatar_url} alt={profile?.full_name}
              style={{ width:36, height:36, borderRadius:'50%', objectFit:'cover', flexShrink:0 }} />
          ) : (
            <div style={{ width:36, height:36, borderRadius:'50%', background:'linear-gradient(135deg,#4F46E5,#6366F1)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:14, fontWeight:700, color:'#fff', flexShrink:0 }}>
              {profile?.full_name?.charAt(0)?.toUpperCase()}
            </div>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{profile?.full_name}</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>Employer · Admin</div>
          </div>
        </div>

        {/* Nav */}
        <nav style={{ flex: 1, padding: '1rem 0.75rem' }}>
          {NAV_GROUPS.map(group => (
            <div key={group.section}>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.5)', letterSpacing: '0.12em', padding: '0 14px', margin: '14px 0 6px', textTransform: 'uppercase' }}>{group.section}</div>
              {group.items.map(item => {
                const active = nav === item.id
                const badge = item.id === 'pdr_approvals' && totalPending > 0 ? totalPending : null
                return (
                  <div key={item.id} className="eh-nav-item" onClick={() => { setNav(item.id); setSelectedEmp(null); setSidebarOpen(false) }} style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '10px 14px', borderRadius: 10, cursor: 'pointer', marginBottom: 3,
                    background: active ? 'rgba(99,102,241,0.25)' : 'transparent',
                    color: active ? '#fff' : 'rgba(255,255,255,0.85)',
                    fontSize: 13, fontWeight: active ? 700 : 500,
                    borderLeft: active ? '3px solid #818CF8' : '3px solid transparent',
                    transition: 'all .15s',
                  }}>
                    <span style={{ fontSize: 18, width: 24, textAlign: 'center', flexShrink: 0, lineHeight: 1 }}>{item.icon}</span>
                    <span style={{ flex: 1, letterSpacing: '0.01em' }}>{item.label}</span>
                    {badge && (
                      <span style={{ fontSize: 10, background: t.amber, color: '#fff', padding: '2px 7px', borderRadius: 99, fontWeight: 700 }}>{badge}</span>
                    )}
                    {item.id === 'reviews' && disputedCount > 0 && (
                      <span style={{ fontSize: 10, background: '#EF4444', color: '#fff', padding: '2px 7px', borderRadius: 99, fontWeight: 700 }}>⚠ {disputedCount}</span>
                    )}
                    {item.id === 'meetings' && pendingMeetingRsvp > 0 && (
                      <span style={{ fontSize: 10, background: '#4F46E5', color: '#fff', padding: '2px 7px', borderRadius: 99, fontWeight: 700 }}>{pendingMeetingRsvp}</span>
                    )}
                  </div>
                )
              })}
            </div>
          ))}

          {/* Team count pill */}
          <div style={{ margin: '1rem 0 0', padding: '10px 12px', background: 'rgba(99,102,241,0.12)', border: '1px solid rgba(99,102,241,0.2)', borderRadius: 8 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#A5B4FC', marginBottom: 4, letterSpacing: '0.06em' }}>TEAM SIZE</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: '#fff' }}>{employees.length} <span style={{ fontSize: 12, fontWeight: 400, color: 'rgba(255,255,255,0.5)' }}>employees</span></div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>{todayIns.length} checked in today</div>
          </div>
        </nav>

        {/* Footer */}
        <div style={{ padding: '1rem 0.75rem', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <div onClick={toggleDark} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 8, cursor: 'pointer', marginBottom: 4, background: 'rgba(255,255,255,0.05)' }}>
            <span style={{ fontSize: 14 }}>{dark ? '☀️' : '🌙'}</span>
            <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)' }}>{dark ? 'Light mode' : 'Dark mode'}</span>
          </div>
          <div onClick={signOut} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 13, color: 'rgba(255,255,255,0.7)' }}>
            <span>←</span> Sign out
          </div>
        </div>
      </div>

      {/* Mobile overlay behind the open sidebar */}
      <div className={`eh-sidebar-overlay${sidebarOpen ? ' show' : ''}`} onClick={() => setSidebarOpen(false)} />

      {/* ══ MAIN ══ */}
      <div className="eh-main" style={{ marginLeft: 250, flex: 1, display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>

        {/* Top bar */}
        <div className="eh-topbar" style={{ background: t.white, borderBottom: `1px solid ${t.border}`, padding: '0 2rem', height: 64, display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 40, boxShadow: t.shadow }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button className="eh-hamburger" onClick={() => setSidebarOpen(true)} aria-label="Menu"
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 22, color: t.text, padding: 4, alignItems: 'center', justifyContent: 'center' }}>☰</button>
            <h1 style={{ fontSize: 18, fontWeight: 700, color: t.text, margin: 0, letterSpacing: '-0.3px' }}>
              {NAV_GROUPS.flatMap(g => g.items).find(n => n.id === nav)?.label || 'Dashboard'}
            </h1>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <NotificationBell t={t} onNavigate={setNav} />
            <span className="eh-hide-mobile" style={{ fontSize: 13, color: t.textMuted }}>
              {new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
            </span>
            <div className="eh-hide-mobile" style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 12px', borderRadius: 99, background: '#EFF6FF', border: '1px solid #BFDBFE' }}>
              <span style={{ fontSize: 11 }}>🏢</span>
              <span style={{ fontSize: 12, fontWeight: 600, color: '#4F46E5' }}>Employer View</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', borderRadius: 10, background: t.bg, border: `1px solid ${t.border}` }}>
              {(fullProfile?.avatar_url || profile?.avatar_url) ? (
                <img src={fullProfile?.avatar_url || profile?.avatar_url} alt={profile?.full_name}
                  style={{ width:28, height:28, borderRadius:'50%', objectFit:'cover' }} />
              ) : (
                <div style={{ width:28, height:28, borderRadius:'50%', background:'linear-gradient(135deg,#4F46E5,#6366F1)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, fontWeight:700, color:'#fff' }}>
                  {profile?.full_name?.charAt(0)}
                </div>
              )}
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: t.text, lineHeight: 1.2 }}>{profile?.full_name}</div>
                <div style={{ fontSize: 11, color: t.textMuted }}>Admin</div>
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <div key={nav} className="eh-view eh-content" style={{ flex: 1, padding: '2rem', overflowY: 'auto' }}>
          {loading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300 }}>
              <p style={{ color: t.textMuted }}>Loading…</p>
            </div>
          ) : (
            <>
              {nav === 'overview'      && <Employeroverview t={t} employees={employees} checkIns={checkIns} todayIns={todayIns} allMeetings={[]} pdrCounts={pdrCounts} profile={profile} />}
              {nav === 'team'          && <Employerteam t={t} employees={employees} checkIns={checkIns} pdrCounts={pdrCounts} orgId={profile.org_id} />}
              {nav === 'checkins'      && <Employercheckins t={t} employees={employees} checkIns={checkIns} />}
              {nav === 'pdr_approvals' && (
                <>
                  {totalPending > 0 && (
                    <div style={{ background:'#FFFBEB', border:'1px solid #FDE68A', borderRadius:12, padding:'12px 18px', marginBottom:16, display:'flex', alignItems:'center', gap:12 }}>
                      <span style={{ fontSize:20 }}>📋</span>
                      <div>
                        <div style={{ fontSize:14, fontWeight:700, color:'#D97706' }}>{totalPending} PDR{totalPending>1?'s':''} pending your approval</div>
                        <div style={{ fontSize:12, color:'#92400E' }}>Review and approve or reject employee PDRs.</div>
                      </div>
                    </div>
                  )}
                  <PDRApprovals orgId={profile.org_id} employees={employees} pdrCounts={pdrCounts} t={t} onRefresh={fetchData} />
                </>
              )}
              {nav === 'meetings' && (
                <>
                  {pendingMeetingRsvp > 0 && (
                    <div style={{ background:'#EEF2FF', border:'1px solid #C7D2FE', borderRadius:12, padding:'12px 18px', marginBottom:16, display:'flex', alignItems:'center', gap:12 }}>
                      <span style={{ fontSize:20 }}>📅</span>
                      <div>
                        <div style={{ fontSize:14, fontWeight:700, color:'#4F46E5' }}>{pendingMeetingRsvp} upcoming meeting{pendingMeetingRsvp>1?'s':''}</div>
                        <div style={{ fontSize:12, color:'#4338CA' }}>Scheduled meetings that need your attention.</div>
                      </div>
                    </div>
                  )}
                  <Meetings profile={profile} t={t} presetCreateDate={meetingPreset} onConsumePreset={() => setMeetingPreset(null)} />
                </>
              )}
              {nav === 'reviews' && (
                <>
                  {disputedCount > 0 && (
                    <div style={{ background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:12, padding:'12px 18px', marginBottom:16, display:'flex', alignItems:'center', gap:12 }}>
                      <span style={{ fontSize:20 }}>⚠️</span>
                      <div>
                        <div style={{ fontSize:14, fontWeight:700, color:'#DC2626' }}>{disputedCount} review{disputedCount>1?'s':''} disputed by employee</div>
                        <div style={{ fontSize:12, color:'#B91C1C' }}>Employee disagrees with scores — open the review to re-score.</div>
                      </div>
                    </div>
                  )}
                  <Reviews profile={profile} t={t} />
                </>
              )}
              {nav === 'calendar'      && <CalendarView t={t} isEmployer onCreateMeeting={(date, time) => { setMeetingPreset({ date, time }); setNav('meetings') }} />}
              {nav === 'jobs'          && <Jobs t={t} isEmployer />}
              {nav === 'expenses'      && <ExpenseLog t={t} isEmployer />}
              {nav === 'planner'       && <EmployerPlanner t={t} profile={profile} />}
              {nav === 'invite'        && <Invite orgId={profile.org_id} onDone={fetchData} t={t} />}
              {nav === 'billing'       && <Billing profile={profile} t={t} />}
              {nav === 'settings'      && <AccountSettings profile={profile} t={t} />}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════════════
// Overview
// ════════════════════════════════════════════════════════════════════════════
function Overview({ t, employees, checkIns, todayIns, pdrCounts, setSelectedEmp }) {
  const totalPending = Object.values(pdrCounts).reduce((s, c) => s + c.pending, 0)
  const today = new Date().toISOString().split('T')[0]

  const stats = [
    { label: 'Total employees', value: employees.length,   color: t.primary, icon: '👥', sub: 'in your org' },
    { label: 'Checked in today', value: todayIns.length,   color: t.success, icon: '✅', sub: `of ${employees.length} employees` },
    { label: 'Pending PDRs',    value: totalPending,        color: t.amber,   icon: '📋', sub: 'awaiting approval' },
    { label: 'Total check-ins', value: checkIns.length,    color: t.purple,  icon: '📊', sub: 'all time' },
  ]

  return (
    <div>
      <div style={{ marginBottom: '1.75rem' }}>
        <h1 style={{ fontSize: 26, fontWeight: 800, color: t.text, margin: '0 0 4px', letterSpacing: '-0.5px' }}>
          Good {getTime()} 👋
        </h1>
        <p style={{ fontSize: 15, color: t.textMuted, margin: 0 }}>Here's your team's performance at a glance</p>
      </div>

      {/* Stat cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14, marginBottom: '1.75rem' }}>
        {stats.map(s => (
          <div key={s.label} style={{ background: t.card, border: `1px solid ${t.cardBorder}`, borderRadius: 14, padding: '1.25rem', boxShadow: t.shadow }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
              <div style={{ fontSize: 28, fontWeight: 800, color: s.color }}>{s.value}</div>
              <div style={{ width: 36, height: 36, borderRadius: 8, background: `${s.color}14`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>{s.icon}</div>
            </div>
            <div style={{ fontSize: 13, fontWeight: 600, color: t.text }}>{s.label}</div>
            <div style={{ fontSize: 11, color: t.textMuted, marginTop: 2 }}>{s.sub}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: '1.5rem' }}>

        {/* Team status today */}
        <div style={{ background: t.card, border: `1px solid ${t.cardBorder}`, borderRadius: 14, padding: '1.5rem', boxShadow: t.shadow }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: t.text, margin: '0 0 1.25rem' }}>Team status today</h3>
          {employees.length === 0 ? (
            <p style={{ fontSize: 13, color: t.textMuted }}>No employees yet — invite someone first.</p>
          ) : (
            <div>
              {employees.slice(0, 8).map(emp => {
                const hasIn = todayIns.some(c => c.employee_id === emp.id)
                return (
                  <div key={emp.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: `1px solid ${t.border}` }}>
                    <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'linear-gradient(135deg, #4F46E5 0%, #6366F1 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: '#fff', flexShrink: 0 }}>
                      {emp.full_name?.charAt(0)}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: t.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{emp.full_name}</div>
                      <div style={{ fontSize: 11, color: t.textMuted }}>{emp.position || emp.email}</div>
                    </div>
                    <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 99, background: hasIn ? t.greenSoft : t.redSoft, color: hasIn ? t.green : t.red, flexShrink: 0 }}>
                      {hasIn ? '✓ In' : 'Absent'}
                    </span>
                  </div>
                )
              })}
              {employees.length > 8 && (
                <p style={{ fontSize: 12, color: t.textMuted, marginTop: 10, textAlign: 'center' }}>+{employees.length - 8} more employees</p>
              )}
            </div>
          )}
        </div>

        {/* Recent check-ins */}
        <div style={{ background: t.card, border: `1px solid ${t.cardBorder}`, borderRadius: 14, padding: '1.5rem', boxShadow: t.shadow }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: t.text, margin: '0 0 1.25rem' }}>Recent check-ins</h3>
          {checkIns.length === 0 ? (
            <p style={{ fontSize: 13, color: t.textMuted }}>No check-ins yet.</p>
          ) : checkIns.slice(0, 6).map(c => (
            <div key={c.id} style={{ display: 'flex', gap: 10, padding: '10px 0', borderBottom: `1px solid ${t.border}` }}>
              <div style={{ width: 34, height: 34, borderRadius: 8, background: `${t.primary}14`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, color: t.primary, flexShrink: 0 }}>
                {c.users?.full_name?.charAt(0)}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: t.text }}>{c.users?.full_name}</span>
                  <span style={{ fontSize: 11, color: t.textMuted, flexShrink: 0 }}>{c.date}</span>
                </div>
                <p style={{ fontSize: 12, color: t.textMuted, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {c.done_today || c.plan_today || '—'}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════════════
// Team
// ════════════════════════════════════════════════════════════════════════════
function Team({ t, employees, checkIns, pdrCounts, setSelectedEmp }) {
  const today = new Date().toISOString().split('T')[0]
  const [search, setSearch] = useState('')
  const filtered = employees.filter(e =>
    e.full_name?.toLowerCase().includes(search.toLowerCase()) ||
    e.email?.toLowerCase().includes(search.toLowerCase())
  )

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.75rem' }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: t.text, margin: '0 0 4px', letterSpacing: '-0.5px' }}>Team</h1>
          <p style={{ fontSize: 14, color: t.textMuted, margin: 0 }}>{employees.length} employees in your organisation</p>
        </div>
        <input
          value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search employees…"
          style={{ padding: '9px 14px', background: t.card, border: `1px solid ${t.border}`, borderRadius: 8, fontSize: 13, color: t.text, width: 220, outline: 'none', fontFamily: 'Inter, system-ui' }}
        />
      </div>

      {filtered.length === 0 ? (
        <div style={{ background: t.card, border: `1px solid ${t.cardBorder}`, borderRadius: 14, padding: '4rem', textAlign: 'center', boxShadow: t.shadow }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>👥</div>
          <p style={{ fontSize: 14, color: t.textMuted }}>No employees found.</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px,1fr))', gap: 14 }}>
          {filtered.map(emp => {
            const empIns = checkIns.filter(c => c.employee_id === emp.id)
            const todayIn = empIns.find(c => c.date === today)
            const pdr = pdrCounts[emp.id] || { pending: 0, total: 0 }
            return (
              <div key={emp.id} style={{ background: t.card, border: `1px solid ${t.cardBorder}`, borderRadius: 14, padding: '1.25rem', boxShadow: t.shadow, position: 'relative' }}>
                {pdr.pending > 0 && (
                  <div style={{ position: 'absolute', top: 14, right: 14, width: 20, height: 20, borderRadius: '50%', background: t.amber, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800, color: '#fff' }}>
                    {pdr.pending}
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: '1rem' }}>
                  <div style={{ width: 46, height: 46, borderRadius: '50%', background: 'linear-gradient(135deg, #4F46E5 0%, #6366F1 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 700, color: '#fff', flexShrink: 0 }}>
                    {emp.full_name?.charAt(0)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: t.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{emp.full_name}</div>
                    <div style={{ fontSize: 12, color: t.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{emp.position || emp.email}</div>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 8, marginBottom: '1rem', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 99, background: todayIn ? t.greenSoft : t.redSoft, color: todayIn ? t.green : t.red }}>
                    {todayIn ? '✓ Checked in today' : '✗ Not checked in'}
                  </span>
                  {pdr.total > 0 && (
                    <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 99, background: `${t.primary}14`, color: t.primary }}>
                      {pdr.total} PDR{pdr.total > 1 ? 's' : ''}
                    </span>
                  )}
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                  <div style={{ fontSize: 12, color: t.textMuted }}>Check-ins: <strong style={{ color: t.text }}>{empIns.length}</strong></div>
                  {emp.department && <div style={{ fontSize: 12, color: t.textMuted }}>{emp.department}</div>}
                </div>

                <button onClick={() => setSelectedEmp(emp)} style={{ width: '100%', padding: '9px', background: t.bg, border: `1px solid ${t.border}`, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', color: t.textSub, fontFamily: 'Inter, system-ui' }}>
                  View profile →
                </button>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════════════
// Employee Detail
// ════════════════════════════════════════════════════════════════════════════
function EmployeeDetail({ emp, checkIns, onBack, orgId, t }) {
  return (
    <div>
      <button onClick={onBack} style={{ fontSize: 13, color: t.textMuted, background: 'none', border: 'none', cursor: 'pointer', marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: 6, fontFamily: 'Inter, system-ui' }}>
        ← Back to team
      </button>

      {/* Header card */}
      <div style={{ background: t.card, border: `1px solid ${t.cardBorder}`, borderRadius: 14, padding: '1.5rem', marginBottom: 16, boxShadow: t.shadow, display: 'flex', alignItems: 'center', gap: 16 }}>
        <div style={{ width: 60, height: 60, borderRadius: '50%', background: 'linear-gradient(135deg, #4F46E5 0%, #6366F1 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, fontWeight: 700, color: '#fff', flexShrink: 0 }}>
          {emp.full_name?.charAt(0)}
        </div>
        <div style={{ flex: 1 }}>
          <h2 style={{ fontSize: 20, fontWeight: 800, color: t.text, margin: '0 0 4px' }}>{emp.full_name}</h2>
          <div style={{ fontSize: 13, color: t.textMuted }}>{emp.email}</div>
          {emp.position && <div style={{ fontSize: 12, color: t.primary, fontWeight: 600, marginTop: 4 }}>{emp.position} {emp.department && `· ${emp.department}`}</div>}
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 11, color: t.textMuted, marginBottom: 2 }}>Total check-ins</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: t.primary }}>{checkIns.length}</div>
        </div>
      </div>

      {/* Recent check-ins */}
      <div style={{ background: t.card, border: `1px solid ${t.cardBorder}`, borderRadius: 14, padding: '1.5rem', boxShadow: t.shadow }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: t.text, margin: '0 0 1rem' }}>Recent check-ins</h3>
        {checkIns.length === 0 ? (
          <p style={{ fontSize: 13, color: t.textMuted }}>No check-ins yet.</p>
        ) : checkIns.slice(0, 8).map(c => (
          <div key={c.id} style={{ padding: '12px 0', borderBottom: `1px solid ${t.border}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: t.text }}>{c.date}</span>
              {c.mood && <span style={{ fontSize: 12, color: t.textMuted, textTransform: 'capitalize' }}>{c.mood}</span>}
            </div>
            {c.plan_today && <p style={{ fontSize: 13, color: t.textSub, margin: '0 0 4px' }}><strong>Plan:</strong> {c.plan_today}</p>}
            {c.done_today && <p style={{ fontSize: 13, color: t.text, margin: '0 0 4px' }}><strong>Done:</strong> {c.done_today}</p>}
            {c.blockers && <p style={{ fontSize: 12, color: t.danger, margin: 0 }}>⚠️ <strong>Blocker:</strong> {c.blockers}</p>}
          </div>
        ))}
      </div>
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════════════
// Check-ins
// ════════════════════════════════════════════════════════════════════════════
function CheckIns({ checkIns, employees, t }) {
  const [filterEmp, setFilterEmp] = useState('all')
  const filtered = filterEmp === 'all' ? checkIns : checkIns.filter(c => c.employee_id === filterEmp)
  const today = new Date().toISOString().split('T')[0]

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.75rem' }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: t.text, margin: '0 0 4px', letterSpacing: '-0.5px' }}>All Check-ins</h1>
          <p style={{ fontSize: 14, color: t.textMuted, margin: 0 }}>{checkIns.length} total entries</p>
        </div>
        <select value={filterEmp} onChange={e => setFilterEmp(e.target.value)}
          style={{ padding: '9px 14px', background: t.card, border: `1px solid ${t.border}`, borderRadius: 8, fontSize: 13, color: t.text, outline: 'none', fontFamily: 'Inter, system-ui', cursor: 'pointer' }}>
          <option value="all">All employees</option>
          {employees.map(e => <option key={e.id} value={e.id}>{e.full_name}</option>)}
        </select>
      </div>

      <div style={{ background: t.card, border: `1px solid ${t.cardBorder}`, borderRadius: 14, padding: '1.25rem', boxShadow: t.shadow }}>
        {filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <div style={{ fontSize: 40, marginBottom: 10 }}>✅</div>
            <p style={{ fontSize: 14, color: t.textMuted }}>No check-ins yet.</p>
          </div>
        ) : filtered.map(c => (
          <div key={c.id} style={{ display: 'flex', gap: 12, padding: '12px 0', borderBottom: `1px solid ${t.border}` }}>
            <div style={{ width: 38, height: 38, borderRadius: 10, background: `${t.primary}14`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, color: t.primary, flexShrink: 0 }}>
              {c.users?.full_name?.charAt(0)}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: t.text }}>{c.users?.full_name}</span>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  {c.date === today && <span style={{ fontSize: 10, fontWeight: 700, background: `${t.primary}14`, color: t.primary, padding: '2px 7px', borderRadius: 99 }}>TODAY</span>}
                  <span style={{ fontSize: 11, color: t.textMuted }}>{c.date}</span>
                </div>
              </div>
              {c.plan_today && <p style={{ fontSize: 12, color: t.textMuted, margin: '0 0 2px' }}><strong>Plan:</strong> {c.plan_today?.slice(0, 120)}{c.plan_today?.length > 120 ? '…' : ''}</p>}
              {c.done_today && <p style={{ fontSize: 12, color: t.textSub, margin: '0 0 2px' }}><strong>Done:</strong> {c.done_today?.slice(0, 120)}{c.done_today?.length > 120 ? '…' : ''}</p>}
              {c.blockers && <p style={{ fontSize: 12, color: t.danger, margin: 0 }}>⚠️ {c.blockers}</p>}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════════════
// PDR Approvals — Employee list → Employee PDRs
// ════════════════════════════════════════════════════════════════════════════
function PDRApprovals({ orgId, employees, pdrCounts, t, onRefresh }) {
  const [selectedEmp, setSelectedEmp] = useState(null)

  if (selectedEmp) {
    return (
      <EmployeePDRs
        employee={selectedEmp}
        orgId={orgId}
        t={t}
        onBack={() => { setSelectedEmp(null); onRefresh() }}
      />
    )
  }

  const totalPending = Object.values(pdrCounts).reduce((s, c) => s + c.pending, 0)

  // Sort: employees with pending PDRs first
  const sorted = [...employees].sort((a, b) => {
    const pa = pdrCounts[a.id]?.pending || 0
    const pb = pdrCounts[b.id]?.pending || 0
    return pb - pa
  })

  return (
    <div>
      <div style={{ marginBottom: '1.75rem' }}>
        <h1 style={{ fontSize: 24, fontWeight: 800, color: t.text, margin: '0 0 4px', letterSpacing: '-0.5px' }}>PDR Approvals</h1>
        <p style={{ fontSize: 14, color: t.textMuted, margin: 0 }}>Review employee performance development cycles — select an employee to view their PDRs</p>
      </div>

      {totalPending > 0 && (
        <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 12, padding: '12px 16px', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 18 }}>⏳</span>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#D97706' }}>
            {totalPending} PDR{totalPending > 1 ? 's' : ''} waiting for your approval
          </span>
        </div>
      )}

      {/* Note about employer role */}
      <div style={{ background: `${t.primary}08`, border: `1px solid ${t.primary}20`, borderRadius: 12, padding: '12px 16px', marginBottom: '1.5rem', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <span style={{ fontSize: 16, flexShrink: 0 }}>ℹ️</span>
        <p style={{ fontSize: 13, color: t.textSub, margin: 0, lineHeight: 1.6 }}>
          You can <strong style={{ color: t.text }}>approve</strong> or <strong style={{ color: t.text }}>reject</strong> PDRs submitted by employees. You cannot edit them directly — if changes are needed, reject the PDR with a reason and ask the employee to delete it and resubmit.
        </p>
      </div>

      {sorted.length === 0 ? (
        <div style={{ background: t.card, border: `1px solid ${t.cardBorder}`, borderRadius: 14, padding: '4rem', textAlign: 'center', boxShadow: t.shadow }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>📋</div>
          <h3 style={{ fontSize: 18, fontWeight: 700, color: t.text, marginBottom: 8 }}>No employees yet</h3>
          <p style={{ fontSize: 14, color: t.textMuted }}>Invite employees so they can submit PDR cycles for review.</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px,1fr))', gap: 14 }}>
          {sorted.map(emp => {
            const pdr = pdrCounts[emp.id] || { pending: 0, total: 0 }
            const hasPending = pdr.pending > 0

            return (
              <div key={emp.id}
                onClick={() => setSelectedEmp(emp)}
                style={{ background: t.card, border: `2px solid ${hasPending ? '#FDE68A' : t.cardBorder}`, borderRadius: 14, padding: '1.25rem', boxShadow: t.shadow, cursor: 'pointer', position: 'relative', transition: 'box-shadow .15s' }}>

                {hasPending && (
                  <div style={{ position: 'absolute', top: -8, right: -8, width: 24, height: 24, borderRadius: '50%', background: t.amber, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, color: '#fff', border: '2px solid white' }}>
                    {pdr.pending}
                  </div>
                )}

                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: '1rem' }}>
                  <div style={{ width: 46, height: 46, borderRadius: '50%', background: 'linear-gradient(135deg, #4F46E5 0%, #6366F1 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 700, color: '#fff', flexShrink: 0 }}>
                    {emp.full_name?.charAt(0)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: t.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{emp.full_name}</div>
                    <div style={{ fontSize: 12, color: t.textMuted }}>{emp.position || emp.email}</div>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 8, marginBottom: '1rem' }}>
                  {pdr.total === 0 ? (
                    <span style={{ fontSize: 12, color: t.textMuted }}>No PDRs submitted yet</span>
                  ) : (
                    <>
                      <span style={{ fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 99, background: `${t.primary}14`, color: t.primary }}>
                        📋 {pdr.total} PDR{pdr.total > 1 ? 's' : ''}
                      </span>
                      {hasPending && (
                        <span style={{ fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 99, background: '#FFFBEB', color: '#D97706', border: '1px solid #FDE68A' }}>
                          ⏳ {pdr.pending} pending
                        </span>
                      )}
                    </>
                  )}
                </div>

                <div style={{ fontSize: 13, color: hasPending ? t.amber : t.primary, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                  {hasPending ? 'Review PDRs →' : 'View PDRs →'}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════════════
// Employee PDRs — shown when employer clicks an employee in PDR Approvals
// ════════════════════════════════════════════════════════════════════════════
function EmployeePDRs({ employee, orgId, t, onBack }) {
  const [pdrs, setPdrs]         = useState([])
  const [loading, setLoading]   = useState(true)
  const [filter, setFilter]     = useState('all')
  const [selectedPdr, setSelectedPdr] = useState(null)
  const [rejectingId, setRejectingId] = useState(null)
  const [rejectReason, setRejectReason] = useState('')
  const [actionLoading, setActionLoading] = useState(false)

  useEffect(() => { fetchPdrs() }, [])

  async function fetchPdrs() {
    setLoading(true)
    try {
      const res = await get(`/pdrs?employee_id=${employee.id}`)
      setPdrs((res.pdrs || []).map((p) => ({ ...p, pdr_goals: p.goals || [] })))
    } catch (e) {
      console.error(e.message)
    }
    setLoading(false)
  }

  async function approve(pdr) {
    setActionLoading(true)
    try {
      await patch(`/pdrs/${pdr.id}`, { status: 'active', rejection_reason: null })
      setSelectedPdr(null); fetchPdrs()
    } catch (e) { alert(e.message) }
    setActionLoading(false)
  }

  async function reject(pdr) {
    if (!rejectReason.trim()) return alert('Please provide a reason so the employee knows what to change.')
    setActionLoading(true)
    try {
      await patch(`/pdrs/${pdr.id}`, { status: 'rejected', rejection_reason: rejectReason })
      setRejectingId(null); setRejectReason(''); fetchPdrs()
    } catch (e) { alert(e.message) }
    setActionLoading(false)
  }

  const STATUS = {
    draft:            { bg: '#F1F5F9', color: '#64748B',  label: 'Draft' },
    pending_approval: { bg: '#FFFBEB', color: '#D97706',  label: 'Pending Approval' },
    active:           { bg: '#ECFDF5', color: '#059669',  label: 'Active' },
    rejected:         { bg: '#FEF2F2', color: '#DC2626',  label: 'Rejected' },
    review_submitted: { bg: '#EFF6FF', color: '#4F46E5',  label: 'Under Review' },
    completed:        { bg: '#F5F3FF', color: '#7C3AED',  label: 'Completed' },
  }

  const FILTERS = [
    { value: 'all',              label: 'All' },
    { value: 'pending_approval', label: 'Pending' },
    { value: 'active',           label: 'Active' },
    { value: 'rejected',         label: 'Rejected' },
    { value: 'completed',        label: 'Completed' },
  ]

  const filtered = filter === 'all' ? pdrs : pdrs.filter(p => p.status === filter)

  return (
    <div>
      {/* Back + header */}
      <button onClick={onBack} style={{ fontSize: 13, color: t.textMuted, background: 'none', border: 'none', cursor: 'pointer', marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: 6, fontFamily: 'Inter, system-ui' }}>
        ← Back to PDR list
      </button>

      {/* Employee card */}
      <div style={{ background: t.card, border: `1px solid ${t.cardBorder}`, borderRadius: 14, padding: '1.25rem 1.5rem', marginBottom: '1.5rem', boxShadow: t.shadow, display: 'flex', alignItems: 'center', gap: 14 }}>
        <div style={{ width: 50, height: 50, borderRadius: '50%', background: 'linear-gradient(135deg, #4F46E5 0%, #6366F1 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, fontWeight: 700, color: '#fff', flexShrink: 0 }}>
          {employee.full_name?.charAt(0)}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 17, fontWeight: 800, color: t.text }}>{employee.full_name}</div>
          <div style={{ fontSize: 13, color: t.textMuted }}>{employee.position || employee.email}</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 11, color: t.textMuted, marginBottom: 2 }}>PDR cycles</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: t.primary }}>{pdrs.length}</div>
        </div>
      </div>

      {/* Read-only notice */}
      <div style={{ background: `${t.primary}08`, border: `1px solid ${t.primary}20`, borderRadius: 12, padding: '10px 16px', marginBottom: '1.25rem', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
        <span style={{ fontSize: 14, flexShrink: 0 }}>🔒</span>
        <p style={{ fontSize: 13, color: t.textSub, margin: 0, lineHeight: 1.6 }}>
          PDRs are <strong style={{ color: t.text }}>read-only</strong> for employers. To request changes, reject the PDR with a clear reason — the employee will need to delete it and resubmit.
        </p>
      </div>

      {/* Filter pills */}
      <div style={{ display: 'flex', gap: 8, marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        {FILTERS.map(f => (
          <button key={f.value} onClick={() => setFilter(f.value)}
            style={{ padding: '6px 14px', borderRadius: 99, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: `1px solid ${filter === f.value ? t.primary : t.cardBorder}`, background: filter === f.value ? `${t.primary}14` : t.card, color: filter === f.value ? t.primary : t.textSub, fontFamily: 'Inter, system-ui' }}>
            {f.label}
            {f.value !== 'all' && (
              <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 800 }}>
                {pdrs.filter(p => p.status === f.value).length || ''}
              </span>
            )}
          </button>
        ))}
      </div>

      {loading ? (
        <p style={{ color: t.textMuted }}>Loading…</p>
      ) : filtered.length === 0 ? (
        <div style={{ background: t.card, border: `1px solid ${t.cardBorder}`, borderRadius: 14, padding: '4rem', textAlign: 'center', boxShadow: t.shadow }}>
          <div style={{ fontSize: 40, marginBottom: 10 }}>📋</div>
          <p style={{ fontSize: 14, color: t.textMuted }}>No PDRs in this category.</p>
        </div>
      ) : selectedPdr ? (
        <EmployeePdrDetail
          pdr={selectedPdr} t={t}
          onBack={() => { setSelectedPdr(null); fetchPdrs() }}
          onApprove={approve} onReject={reject}
          rejectingId={rejectingId} setRejectingId={setRejectingId}
          rejectReason={rejectReason} setRejectReason={setRejectReason}
          actionLoading={actionLoading}
        />
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:14 }}>
        {filtered.map((pdr, idx) => {
          const s = STATUS[pdr.status] || STATUS.draft
          const goals = (pdr.pdr_goals || []).sort((a, b) => a.goal_number - b.goal_number)
          const totalWeight = goals.reduce((sum, g) => sum + (g.weightage || 0), 0)
          const isPending = pdr.status === 'pending_approval'
          const overall = Math.round(goals.length ? goals.reduce((s,g) => s + ((g.progress||0)*((g.weightage||0)/100)), 0) : 0)
          return (
            <div key={pdr.id} onClick={() => setSelectedPdr(pdr)}
              style={{ background:t.card, border:`2px solid ${isPending?'#FDE68A':t.cardBorder}`, borderRadius:14, padding:'1.25rem', boxShadow:t.shadow, cursor:'pointer', position:'relative', transition:'box-shadow .15s' }}>
              {isPending && (
                <div style={{ position:'absolute', top:-8, right:-8, width:22, height:22, borderRadius:'50%', background:'#F59E0B', display:'flex', alignItems:'center', justifyContent:'center', fontSize:10, fontWeight:800, color:'#fff', border:'2px solid white' }}>!</div>
              )}
              <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
                <div style={{ width:36, height:36, borderRadius:9, background:'linear-gradient(135deg,#4F46E5,#6366F1)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, fontWeight:800, color:'#fff', flexShrink:0 }}>
                  {idx+1}
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:14, fontWeight:700, color:t.text, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{pdr.title}</div>
                  <div style={{ fontSize:11, color:t.textMuted, textTransform:'capitalize' }}>{pdr.review_type} · {pdr.start_date} → {pdr.end_date}</div>
                </div>
              </div>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                <span style={{ fontSize:11, fontWeight:600, background:s.bg, color:s.color, padding:'3px 10px', borderRadius:99, border:`1px solid ${s.color}30` }}>{s.label}</span>
                <span style={{ fontSize:12, fontWeight:800, color:overall>=60?'#059669':overall>=30?'#D97706':'#EF4444' }}>{overall}%</span>
              </div>
              <div style={{ background:t.border, borderRadius:99, height:6, overflow:'hidden', marginBottom:8 }}>
                <div style={{ width:`${overall}%`, height:'100%', background:'linear-gradient(90deg,#4F46E5,#6366F1)', borderRadius:99 }} />
              </div>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <span style={{ fontSize:11, color:t.textMuted }}>{goals.length} goal{goals.length!==1?'s':''}</span>
                <span style={{ fontSize:12, fontWeight:600, color:isPending?'#D97706':t.primary }}>
                  {isPending ? 'Review now →' : 'View →'}
                </span>
              </div>
            </div>
          )
        })}
        </div>
      )}
    </div>
  )
}

// ── Employee PDR Detail (employer view) ──────────────────────────────────
function EmployeePdrDetail({ pdr, t, onBack, onApprove, onReject, rejectingId, setRejectingId, rejectReason, setRejectReason, actionLoading }) {
  const STATUS_MAP = {
    draft:            { bg: '#F1F5F9', color: '#64748B',  label: 'Draft' },
    pending_approval: { bg: '#FFFBEB', color: '#D97706',  label: 'Pending Approval' },
    active:           { bg: '#ECFDF5', color: '#059669',  label: 'Active' },
    rejected:         { bg: '#FEF2F2', color: '#DC2626',  label: 'Rejected' },
    review_submitted: { bg: '#EFF6FF', color: '#4F46E5',  label: 'Under Review' },
    completed:        { bg: '#F5F3FF', color: '#7C3AED',  label: 'Completed' },
  }
  const GOAL_THEMES = [
    { color:'#4F46E5', bg:'#EEF2FF', border:'#C7D2FE' },
    { color:'#059669', bg:'#ECFDF5', border:'#A7F3D0' },
    { color:'#D97706', bg:'#FFFBEB', border:'#FDE68A' },
    { color:'#7C3AED', bg:'#F5F3FF', border:'#DDD6FE' },
    { color:'#DB2777', bg:'#FDF2F8', border:'#FBCFE8' },
    { color:'#0EA5E9', bg:'#F0F9FF', border:'#BAE6FD' },
    { color:'#EF4444', bg:'#FEF2F2', border:'#FECACA' },
    { color:'#F97316', bg:'#FFF7ED', border:'#FED7AA' },
  ]
  function gt(i) { return GOAL_THEMES[i % GOAL_THEMES.length] }

  const s = STATUS_MAP[pdr.status] || STATUS_MAP.draft
  const goals = (pdr.pdr_goals || []).sort((a,b) => a.goal_number - b.goal_number)
  const totalWeight = goals.reduce((sum,g) => sum+(g.weightage||0), 0)
  const isPending = pdr.status === 'pending_approval'
  const weighted = Math.round(goals.reduce((s,g) => s+((g.progress||0)*((g.weightage||0)/100)), 0))

  return (
    <div>
      <button onClick={onBack} style={{ fontSize:13, color:t.textMuted, background:'none', border:'none', cursor:'pointer', marginBottom:'1.25rem', display:'flex', alignItems:'center', gap:6, fontFamily:'Inter,system-ui' }}>
        ← Back to PDR list
      </button>

      {/* PDR header */}
      <div style={{ background:t.card, border:`1px solid ${isPending?'#FDE68A':t.cardBorder}`, borderRadius:14, padding:'1.25rem 1.5rem', marginBottom:'1rem', boxShadow:t.shadow }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
          <div>
            <h3 style={{ fontSize:18, fontWeight:800, color:t.text, margin:'0 0 6px' }}>{pdr.title}</h3>
            <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
              <span style={{ fontSize:12, fontWeight:600, background:`${t.primary}14`, color:t.primary, padding:'3px 10px', borderRadius:99, textTransform:'capitalize' }}>{pdr.review_type}</span>
              <span style={{ fontSize:12, color:t.textMuted }}>📅 {pdr.start_date} → {pdr.end_date}</span>
              {pdr.status === 'active' && <span style={{ fontSize:12, fontWeight:700, color:weighted>=60?'#059669':'#D97706' }}>Overall: {weighted}%</span>}
            </div>
          </div>
          <span style={{ fontSize:12, fontWeight:600, background:s.bg, color:s.color, padding:'5px 14px', borderRadius:99, border:`1px solid ${s.color}30`, flexShrink:0 }}>{s.label}</span>
        </div>
      </div>

      {/* Goals */}
      <div style={{ background:t.card, border:`1px solid ${t.cardBorder}`, borderRadius:14, padding:'1.25rem 1.5rem', marginBottom:'1rem', boxShadow:t.shadow }}>
        <div style={{ fontSize:11, fontWeight:700, color:t.textMuted, textTransform:'uppercase', letterSpacing:'.06em', marginBottom:12 }}>Goals (read-only)</div>
        {goals.length === 0 ? (
          <p style={{ fontSize:13, color:t.textMuted }}>No goals attached.</p>
        ) : goals.map((g, gi) => {
          const theme = gt(gi)
          const prog  = g.progress || 0
          const pCol  = prog>=90?'#059669':prog>=60?'#4F46E5':prog>=30?'#D97706':'#EF4444'
          return (
            <div key={g.id} style={{ marginBottom:12, border:`1px solid ${theme.border}`, borderLeft:`5px solid ${theme.color}`, borderRadius:12, overflow:'hidden' }}>
              <div style={{ background:theme.bg, padding:'10px 14px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                  <div style={{ width:26, height:26, borderRadius:'50%', background:theme.color, display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:900, color:'#fff', flexShrink:0 }}>{g.goal_number}</div>
                  <div>
                    <div style={{ fontSize:13, fontWeight:700, color:'#1E293B' }}>{g.title}</div>
                    {g.kpi && <div style={{ fontSize:11, color:'#64748B', marginTop:1 }}>KPI: {g.kpi}</div>}
                  </div>
                </div>
                <div style={{ display:'flex', gap:8, alignItems:'center', flexShrink:0 }}>
                  <span style={{ fontSize:11, fontWeight:700, background:`${theme.color}20`, color:theme.color, padding:'2px 9px', borderRadius:99 }}>{g.weightage}% weight</span>
                  <span style={{ fontSize:13, fontWeight:900, color:pCol }}>{prog}%</span>
                </div>
              </div>
              <div style={{ padding:'10px 14px', background:'#fff' }}>
                <div style={{ background:'#E5E7EB', borderRadius:99, height:9, overflow:'hidden', marginBottom:6 }}>
                  <div style={{ width:`${prog}%`, height:'100%', background:`linear-gradient(90deg,${theme.color},${theme.color}cc)`, borderRadius:99, transition:'width .4s' }} />
                </div>
                {g.progress_notes && <div style={{ fontSize:11, color:'#475569', fontStyle:'italic', background:'#F8F9FA', borderRadius:7, padding:'5px 9px' }}>💬 {g.progress_notes}</div>}
                {g.last_updated_at && <div style={{ fontSize:10, color:'#9CA3AF', marginTop:4 }}>Updated {new Date(g.last_updated_at).toLocaleDateString('en-US',{month:'short',day:'numeric'})}</div>}
              </div>
            </div>
          )
        })}
        <div style={{ display:'flex', justifyContent:'flex-end', paddingTop:8 }}>
          <span style={{ fontSize:13, fontWeight:700, color:totalWeight===100?t.success:t.danger }}>Total weightage: {totalWeight}%</span>
        </div>
      </div>

      {/* Rejection reason */}
      {pdr.status === 'rejected' && pdr.rejection_reason && (
        <div style={{ background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:10, padding:'12px 14px', fontSize:13, color:'#DC2626', marginBottom:'1rem' }}>
          <strong>Rejection reason:</strong> {pdr.rejection_reason}
        </div>
      )}

      {/* Approve/Reject actions */}
      {isPending && (
        <div style={{ background:t.card, border:`1px solid ${t.cardBorder}`, borderRadius:14, padding:'1.25rem 1.5rem', boxShadow:t.shadow }}>
          {rejectingId === pdr.id ? (
            <div>
              <label style={{ display:'block', fontSize:13, fontWeight:600, color:t.text, marginBottom:6 }}>Rejection reason *</label>
              <textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)}
                placeholder="e.g. Please reduce goal 1 weightage to 30% and add a clearer KPI for goal 3."
                rows={3}
                style={{ width:'100%', padding:'10px 12px', background:t.input, border:`1px solid ${t.inputBorder}`, borderRadius:8, fontSize:13, color:t.inputText, boxSizing:'border-box', marginBottom:10, resize:'vertical', fontFamily:'Inter,system-ui' }} />
              <div style={{ display:'flex', gap:8 }}>
                <button onClick={() => onReject(pdr)} disabled={actionLoading}
                  style={{ padding:'9px 20px', background:t.danger, color:'#fff', border:'none', borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:'Inter,system-ui' }}>
                  {actionLoading ? 'Rejecting…' : 'Confirm rejection'}
                </button>
                <button onClick={() => { setRejectingId(null); setRejectReason('') }}
                  style={{ padding:'9px 20px', background:'transparent', border:`1px solid ${t.border}`, borderRadius:8, fontSize:13, fontWeight:600, color:t.textSub, cursor:'pointer', fontFamily:'Inter,system-ui' }}>
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div style={{ display:'flex', gap:10, alignItems:'center', flexWrap:'wrap' }}>
              <button onClick={() => onApprove(pdr)} disabled={totalWeight!==100||actionLoading}
                style={{ padding:'10px 22px', background:totalWeight===100?t.success:t.border, color:totalWeight===100?'#fff':t.textMuted, border:'none', borderRadius:8, fontSize:14, fontWeight:700, cursor:totalWeight===100?'pointer':'not-allowed', fontFamily:'Inter,system-ui' }}>
                ✓ Approve PDR
              </button>
              <button onClick={() => setRejectingId(pdr.id)}
                style={{ padding:'10px 22px', background:'transparent', border:`1px solid ${t.danger}`, borderRadius:8, fontSize:14, fontWeight:700, color:t.danger, cursor:'pointer', fontFamily:'Inter,system-ui' }}>
                ✕ Reject & Ask to Revise
              </button>
              {totalWeight!==100 && <span style={{ fontSize:12, color:t.textMuted }}>⚠️ Weightages total {totalWeight}%, not 100%</span>}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════════════
// Invite
// ════════════════════════════════════════════════════════════════════════════
function Invite({ orgId, onDone, t }) {
  const [form, setForm]     = useState({ fullName: '', email: '', password: '' })
  const [status, setStatus] = useState('')
  const [loading, setLoading] = useState(false)

  async function handleInvite(e) {
    e.preventDefault()
    setLoading(true)
    setStatus('')
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, orgId, role: 'employee' }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error)
      setStatus('success')
      setForm({ fullName: '', email: '', password: '' })
      onDone()
    } catch (err) {
      setStatus(err.message)
    }
    setLoading(false)
  }

  const IS = { display: 'block', width: '100%', padding: '11px 14px', background: t.input, border: `1px solid ${t.inputBorder}`, borderRadius: 8, fontSize: 14, color: t.inputText, boxSizing: 'border-box', marginBottom: '1rem', fontFamily: 'Inter, system-ui', outline: 'none' }
  const LS = { display: 'block', fontSize: 13, fontWeight: 600, color: t.text, marginBottom: 6 }

  return (
    <div>
      <div style={{ marginBottom: '1.75rem' }}>
        <h1 style={{ fontSize: 24, fontWeight: 800, color: t.text, margin: '0 0 4px', letterSpacing: '-0.5px' }}>Invite Employee</h1>
        <p style={{ fontSize: 14, color: t.textMuted, margin: 0 }}>Add a new employee to your organisation</p>
      </div>

      <div style={{ background: t.card, border: `1px solid ${t.cardBorder}`, borderRadius: 14, padding: '2rem', maxWidth: 460, boxShadow: t.shadow }}>
        {status === 'success' ? (
          <div style={{ textAlign: 'center', padding: '1.5rem 0' }}>
            <div style={{ fontSize: 48, marginBottom: '1rem' }}>✅</div>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: t.text, marginBottom: 8 }}>Employee added!</h3>
            <p style={{ fontSize: 14, color: t.textMuted, marginBottom: '1.5rem' }}>They can now log in with their email and password.</p>
            <button onClick={() => setStatus('')} style={{ padding: '10px 22px', background: `linear-gradient(135deg, ${t.primary} 0%, ${t.primary2} 100%)`, color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600, fontFamily: 'Inter, system-ui' }}>
              Add another →
            </button>
          </div>
        ) : (
          <form onSubmit={handleInvite}>
            <div>
              <label style={LS}>Full name</label>
              <input value={form.fullName} onChange={e => setForm({ ...form, fullName: e.target.value })} placeholder="Jane Smith" required style={IS} />
            </div>
            <div>
              <label style={LS}>Work email</label>
              <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="jane@company.com" required style={IS} />
            </div>
            <div>
              <label style={LS}>Temporary password</label>
              <input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="Min 6 characters" required minLength={6} style={IS} />
              <p style={{ fontSize: 12, color: t.textMuted, margin: '-8px 0 12px' }}>Employee can change this after their first login.</p>
            </div>
            {status && <p style={{ fontSize: 13, color: t.danger, marginBottom: '1rem' }}>{status}</p>}
            <button type="submit" disabled={loading}
              style={{ width: '100%', padding: '12px', background: `linear-gradient(135deg, ${t.primary} 0%, ${t.primary2} 100%)`, color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'Inter, system-ui', boxShadow: '0 2px 8px rgba(79,70,229,0.3)' }}>
              {loading ? 'Adding employee…' : 'Add employee →'}
            </button>
          </form>
        )}
      </div>
    </div>
  )
}
